import { FormEvent, useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { useIntl } from 'react-intl';

import { Button, FontSizes, FontWeights, FormInput, Loader, Text } from '@calm-web/design-system';

import {
	CAMPAIGN_FUNNEL_EVENTS,
	campaignEventProps,
	campaignPartnershipEventProps,
	type CampaignEventIdentity,
	type CampaignPartnershipIdentity,
} from '@/components/SimplifiedSignup/configs/Campaigns/analytics';
import { useAnalytics } from '@/hooks/analytics';
import { postValidateCampaignCode, type CampaignCodeValidationReason } from '@/utils/endpoints';

import messages from './messages';
import { CampaignCodeEntryWrapper } from './styles';

// Deriving from the endpoint's own reason type (rather than hand-listing the four values
// again) means a future backend reason value fails to compile here instead of silently
// falling through FAILURE_REASON_MESSAGES as undefined.
type FailureReason = NonNullable<CampaignCodeValidationReason> | 'generic';

const FAILURE_REASON_MESSAGES: Record<FailureReason, typeof messages.invalid> = {
	invalid: messages.invalid,
	expired: messages.expired,
	already_used: messages.alreadyUsed,
	deleted: messages.deleted,
	generic: messages.generic,
};

// The response's `reason` is only type-checked at compile time; nothing validates it at
// runtime, so a backend value outside these four (a new reason shipped ahead of this
// frontend, a typo) must fall back to the generic message rather than crash formatMessage
// with an unrecognized key.
const isRecognizedReason = (
	value: CampaignCodeValidationReason,
): value is NonNullable<CampaignCodeValidationReason> => value !== null && value in FAILURE_REASON_MESSAGES;

// The app's Axios interceptor rejects an HTTP failure with the response itself, so the status sits
// at the top level. A raw Axios error still nests it under response. A network error has neither.
const getResponseStatus = (error: unknown): number | null => {
	if (typeof error !== 'object' || error === null) return null;
	const { status, response } = error as { status?: number; response?: { status?: number } };
	return status ?? response?.status ?? null;
};

interface CampaignCodeEntryProps {
	/**
	 * Addresses the validate-code call and identifies the campaign on every funnel event logged
	 * here. `bypass_payment` feeds the partnership type on those events.
	 */
	campaign: CampaignEventIdentity & Pick<CampaignPartnershipIdentity, 'bypass_payment'>;
	requiresCode: boolean;
	initialCode?: string;
	contactEmail: string | null;
	onValidated: (code: string | undefined) => void;
}

const CampaignCodeEntry = ({
	campaign,
	requiresCode,
	initialCode,
	contactEmail,
	onValidated,
}: CampaignCodeEntryProps): JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { logEvent } = useAnalytics();
	const campaignId = campaign.id;
	// Memoised so the validate() dependency list below stays stable across parent re-renders.
	const eventProps = useMemo(() => campaignEventProps(campaign), [campaign]);
	const [code, setCode] = useState(initialCode ?? '');
	const [reason, setReason] = useState<FailureReason | undefined>(undefined);
	// Lazily seeded so a prefilled code's auto-validation is already "loading" on the very
	// first paint — otherwise the interactive, prefilled form would render for one frame
	// before the effect below sets this, both flashing and briefly leaving a live Submit
	// button sitting unattended.
	const [isLoading, setIsLoading] = useState(() => requiresCode && Boolean(initialCode));
	const hasSkippedValidation = useRef(false);
	const hasAutoValidated = useRef(false);
	// Tracks whether the form has ever actually been shown. Only the pre-interaction
	// auto-validate load (never true here yet) hides the form behind a full loader; any
	// later load (a manual submit) keeps the form mounted and uses the submit button's own
	// isLoading spinner instead, matching PartnershipValidation's manual-submit UX.
	const hasShownForm = useRef(false);

	const validate = useCallback(
		async (codeToValidate: string, isPrefilled: boolean) => {
			setIsLoading(true);
			setReason(undefined);
			// The block's promo_code is the code being validated, not the link code the store still
			// holds at this point, so a typed code is attributed on its own validation events.
			const validationEventProps = {
				...eventProps,
				...campaignPartnershipEventProps({ ...campaign, offer_code: codeToValidate }),
				is_prefilled: isPrefilled,
			};
			logEvent({
				eventName: CAMPAIGN_FUNNEL_EVENTS.codeValidationSubmitted,
				eventProps: validationEventProps,
			});
			try {
				const { data } = await postValidateCampaignCode({ campaignId, code: codeToValidate });
				if (data.valid) {
					logEvent({
						eventName: CAMPAIGN_FUNNEL_EVENTS.codeValidationSuccess,
						eventProps: validationEventProps,
					});
					onValidated(codeToValidate);
				} else {
					// The backend's own reason, not the message key it maps to. A reason this build does
					// not recognize still shows the generic copy, but the event names it, so the gap is visible.
					logEvent({
						eventName: CAMPAIGN_FUNNEL_EVENTS.codeValidationFailure,
						eventProps: { ...validationEventProps, reason: data.reason ?? 'unknown' },
					});
					setReason(isRecognizedReason(data.reason) ? data.reason : 'generic');
				}
			} catch (error) {
				// Kept apart from a declined code. A spike here is the endpoint (rate limiting, an
				// outage), not users mistyping codes.
				logEvent({
					eventName: CAMPAIGN_FUNNEL_EVENTS.codeValidationFailure,
					eventProps: { ...validationEventProps, reason: 'request_failed', status: getResponseStatus(error) },
				});
				setReason('generic');
			} finally {
				setIsLoading(false);
			}
		},
		[campaign, campaignId, eventProps, logEvent, onValidated],
	);

	// A campaign that doesn't require a code has nothing for this component to render, but the
	// caller still needs to be told there's no code to carry forward.
	useEffect(() => {
		if (requiresCode || hasSkippedValidation.current) return;
		hasSkippedValidation.current = true;
		onValidated(undefined);
	}, [requiresCode, onValidated]);

	// Runs once per mount to auto-validate a prefilled code. The ref guards against re-firing on
	// re-renders triggered by validate() itself (e.g. setIsLoading).
	useEffect(() => {
		if (!requiresCode || !initialCode || hasAutoValidated.current) return;
		hasAutoValidated.current = true;
		// validate() handles its own rejections internally; this catch only satisfies the
		// no-floating-promises lint rule.
		validate(initialCode, true).catch(() => {});
	}, [requiresCode, initialCode, validate]);

	const showLoaderOnly = isLoading && !hasShownForm.current;

	// Marking "shown" after commit, not during render, so a render that never actually
	// commits (e.g. Strict Mode's double-render in development) can't flip this before the
	// form has actually been painted for the user. Kept above the requiresCode early return
	// below so this hook still runs on every render, per the rules of hooks.
	useEffect(() => {
		if (!showLoaderOnly) {
			hasShownForm.current = true;
		}
	}, [showLoaderOnly]);

	if (!requiresCode) return null;

	const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
		e.preventDefault();
		validate(code, false).catch(() => {});
	};

	if (showLoaderOnly) {
		return (
			<CampaignCodeEntryWrapper>
				<Loader color="blue4" />
			</CampaignCodeEntryWrapper>
		);
	}

	return (
		<CampaignCodeEntryWrapper>
			<form onSubmit={handleSubmit}>
				<FormInput
					name="code"
					label={formatMessage(messages.codeInputLabel)}
					noValidation
					value={code}
					onChange={e => setCode(e.currentTarget.value)}
				/>
				<Button
					backgroundColor="buttonPrimaryBg"
					type="submit"
					isDisabled={!code.trim()}
					isLoading={isLoading}
				>
					{formatMessage(messages.submitCta)}
				</Button>
			</form>
			{reason && (
				// role="status" carries an implicit aria-live="polite", unlike role="alert" (which
				// implies "assertive") — matching the explicit aria-live here instead of
				// contradicting it.
				<Text role="status" aria-live="polite" size={FontSizes.sm} weight={FontWeights.Light}>
					{formatMessage(FAILURE_REASON_MESSAGES[reason])}
				</Text>
			)}
			{reason && contactEmail && (
				<Text role="status" aria-live="polite" size={FontSizes.sm} weight={FontWeights.Light}>
					{formatMessage(messages.contactPrompt, { contactEmail })}
				</Text>
			)}
		</CampaignCodeEntryWrapper>
	);
};

export default CampaignCodeEntry;
